import React, { useState, useEffect } from "react";
import { useLedger } from "../hooks/useLedger";
import CommonButtons from "../../../../../../shared/components/CommonButtons";
import { ModalWrapper } from "../../../../../../shared/components/ModalWrapper"; // adjust path as needed
import { CustomerData } from "src/shared/services/interfacev2";
import { useUser } from "src/shared/context/UserContext";
import { notifyWarning } from "src/assets/toastService";
import CreditLimitRobo from "src/assets/design/Creditlimitrobo";

interface Props {
  customer: CustomerData;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
  onAddProduct: () => void;
}

const CustomerLedger: React.FC<Props> = ({
  customer,
  onBack,
  onConfirm,
  onClose,
  onAddProduct,
}) => {
  const { ledger, updatesLedgerLimit, loading } = useLedger(customer);
  const { creditLimit, userConfigration, user } = useUser();
  const modifyCreditLimit = user?.role?.[0] === "Owner" || user?.role?.[0] === "Admin";

  const isCreditControlEnabled = (userConfigration?.creditControlEnabled && modifyCreditLimit) || false;

  const criditLimitValueCheck = () => {
    if (isCreditControlEnabled && ledger?.defaultCreditControl === true) {
      return creditLimit.defaultLimit;
    } else if (isCreditControlEnabled && ledger?.defaultCreditControl === false) {
      return ledger?.creditLimit?.defaultLimit || 0;
    } else {
      return null;
    }
  };

  const creditLimits = {
    defaultLimit: criditLimitValueCheck(),
    maxLimit: creditLimit.maxLimit,
    lowBalanceAlert: creditLimit.lowBalanceAlert,
  };

  const handleUpdateLimit = (changeLimit: number) => {
    if (updatesLedgerLimit) {
      updatesLedgerLimit(customer._id.$oid, Number(changeLimit));
    }
  };

  const outstandingBalance = Number(ledger?.totalOutstandingBalance) || 0;
  const creditLimitValue = Number(creditLimits.defaultLimit) || 0;
  const lowBalanceAlertPct = Number(creditLimits.lowBalanceAlert) || 0;

  const alertValue = (creditLimitValue / 100) * lowBalanceAlertPct;

  const availableCredit =
    outstandingBalance < 0 ? creditLimitValue : creditLimitValue - outstandingBalance;

  const getBalanceStyle = () => {
    if (outstandingBalance < 0) return "text-blue-600 font-bold"; // advance payment
    if (creditLimitValue > 0 && outstandingBalance > creditLimitValue)
      return "text-red-600 font-bold"; // over limit
    if (creditLimitValue > 0 && availableCredit < alertValue)
      return "text-yellow-600 font-bold"; // near limit / low balance alert
    return "text-green-600 font-bold"; // healthy
  };

  const rows = [
    {
      label: "Customer Name",
      value: [customer?.firstName, customer?.middleName, customer?.lastName]
        .filter(Boolean)
        .join(" "),
    },
    { label: "Phone Number", value: customer?.phones?.[0]?.phone ?? "-" },
    {
      label: outstandingBalance < 0 ? "Advanced Payment" : "Outstanding Balance",
      value: `${Math.abs(outstandingBalance).toFixed(2)} QAR`,
      className: getBalanceStyle(),
    },
  ];

  return (
    <ModalWrapper
      title="Customer Ledger"
      onClose={onClose}
      showBackButton={true}
      onBack={onBack}
      width="max-w-xl"
      className="min-w-[450px] text-center"
    >
      <div className="flex justify-end">
        <CommonButtons label="Add Product" variant="primary" onClick={onAddProduct} className="w-36" />
      </div>

      <div className="p-4 rounded-lg mb-3 justify-center">
        <ul className="space-y-3 text-center">
          {rows.map((row, idx) => (
            <li key={idx} className="flex justify-between min-w-[250px]">
              <span>{row.label}:</span>
              <span className={`text-gray-700 ${row.className || ""}`}>{row.value}</span>
            </li>
          ))}
        </ul>

        {isCreditControlEnabled && creditLimitValue > 0 && (
          <div className="mt-2 p-2 rounded-lg border border-gray-300">
            <CreditLimitRobo
              outstandingBalance={outstandingBalance}
              creditLimit={creditLimitValue}
              currency="QAR"
              locale="en-IN"
              onLimitChange={handleUpdateLimit}
              loading={loading}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-4 ">
        <CommonButtons label="Cancel" variant="outlineDanger" onClick={onBack} className="min-w-[130px]" />
        <CommonButtons label="Confirm" onClick={onConfirm} className="min-w-[130px]" />
      </div>
    </ModalWrapper>
  );
};

export default CustomerLedger;